/**
 * Tâche 1312 (Eric 2026-06-30) — Blocs de boutons repliables.
 *
 * Les barres de l'onglet Transcriptions portent entre 9 et 14 boutons. La
 * plupart ne servent qu'une fois dans la vie d'une vidéo (« Purger doublons »,
 * « Reparser », « Réappliquer diarisation micro-tours »). Chaque bloc — celui
 * que le libellé « Fusion : », « Orthographe : », « Whisper : »… ouvre — peut
 * donc être replié d'un clic.
 *
 * Choix assumés :
 *   • **rien n'est replié au départ** : personne ne doit découvrir qu'un
 *     bouton a disparu sans l'avoir demandé ;
 *   • le choix est **mémorisé** dans le navigateur, sinon il faudrait le
 *     refaire à chaque fichier — c'est précisément ce qu'on veut éviter ;
 *   • un bloc replié annonce **combien** de boutons il cache : « Fusion : (5) »
 *     dit qu'il reste quelque chose derrière.
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "knoria.collapsedToolbarGroups.v1";
const EVENT = "knoria:toolbar-groups-changed";

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((k) => typeof k === "string") : [];
  } catch (_) {
    return [];
  }
}

function write(list) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch (_) { /* stockage refusé : le pli reste valable pour la session */ }
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (_) { /* environnement sans CustomEvent (tests) */ }
}

export function useCollapsedToolbarGroups() {
  const [collapsed, setCollapsed] = useState(() => read());

  // Plusieurs barres coexistent à l'écran (ligne 1 et ligne 2) : sans cet
  // écouteur, replier un bloc n'en rafraîchirait qu'une.
  useEffect(() => {
    const onChange = () => setCollapsed(read());
    window.addEventListener(EVENT, onChange);
    return () => window.removeEventListener(EVENT, onChange);
  }, []);

  const isCollapsed = useCallback((key) => collapsed.includes(key), [collapsed]);

  const toggleGroup = useCallback((key) => {
    const next = read().includes(key)
      ? read().filter((k) => k !== key)
      : [...read(), key];
    write(next);
    setCollapsed(next);
  }, []);

  return { isCollapsed, toggleGroup };
}

export default useCollapsedToolbarGroups;
